import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, RotateCcw, Mail, ExternalLink, Smartphone, Monitor, Download, Code, Clock, DollarSign, Check, Sparkles, FileCode, Package, Zap } from 'lucide-react';
import { playSound, SOUNDS } from '../App';

// Import all preview apps
import DashboardPreview from './previews/DashboardPreview';
import KanbanPreview from './previews/KanbanPreview';
import ContentPreview from './previews/ContentPreview';
import FitnessPreview from './previews/FitnessPreview';
import FoodPreview from './previews/FoodPreview';
import ChatPreview from './previews/ChatPreview';

const previewComponents = {
  dashboard: DashboardPreview,
  kanban: KanbanPreview,
  content: ContentPreview,
  fitness: FitnessPreview,
  food: FoodPreview,
  chat: ChatPreview,
};

export default function AppPreviewContainer({ app, platform, customization, onBack, onRestart, onContact }) {
  const [showCodeModal, setShowCodeModal] = useState(false);
  const PreviewComponent = previewComponents[app?.id];
  const theme = customization?.theme || { primary: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' };
  const companyName = customization?.companyName || 'Your Company';

  const techStack = {
    desktop: ['React 18', 'TypeScript', 'TailwindCSS', 'Node.js', 'PostgreSQL', 'Redis'],
    mobile: ['React Native', 'TypeScript', 'Expo', 'Node.js', 'MongoDB', 'Firebase']
  };

  const estimates = {
    dashboard: { time: '4-6 weeks', price: 'From $15,000' },
    kanban: { time: '5-7 weeks', price: 'From $18,000' },
    content: { time: '6-8 weeks', price: 'From $22,000' },
    fitness: { time: '6-8 weeks', price: 'From $25,000' },
    food: { time: '8-10 weeks', price: 'From $30,000' },
    chat: { time: '4-6 weeks', price: 'From $20,000' },
  };

  const estimate = estimates[app?.id] || { time: '4-8 weeks', price: 'Contact us' };

  return (
    <motion.div
      key="preview"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0 }}
      style={{ 
        minHeight: '520px',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Top Bar */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 20px',
        background: 'rgba(255,255,255,0.05)',
        borderBottom: '1px solid rgba(255,255,255,0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <motion.div
            animate={{ rotate: [0, 5, -5, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '10px',
              background: theme.gradient,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 4px 15px ${theme.primary}40`
            }}
          >
            {platform === 'desktop' ? <Monitor size={18} color="white" /> : <Smartphone size={18} color="white" />}
          </motion.div>
          <div>
            <h3 style={{ 
              margin: 0, 
              fontSize: '14px', 
              color: 'white',
              fontFamily: "'Segoe UI', sans-serif"
            }}>
              {companyName} - {app?.name}
            </h3>
            <p style={{ 
              margin: 0, 
              fontSize: '10px', 
              color: '#888',
              fontFamily: "'Segoe UI', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <Sparkles size={10} color={theme.primary} />
              Built with Autonomous AI • Interactive Preview
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <motion.button
            onClick={() => { playSound(SOUNDS.click); setShowCodeModal(true); }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '8px 14px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '6px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '11px',
              fontFamily: "'Segoe UI', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Code size={12} />
            View Details
          </motion.button>
          <motion.button
            onClick={() => { playSound(SOUNDS.click); onRestart(); }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '8px 14px',
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '6px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '11px',
              fontFamily: "'Segoe UI', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <RotateCcw size={12} />
            Build Another
          </motion.button>
        </div>
      </div>

      {/* Preview Area */}
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '20px',
        background: `linear-gradient(135deg, #1a1a2e 0%, ${theme.primary}10 100%)`
      }}>
        {platform === 'mobile' ? (
          // Mobile Phone Frame
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            style={{
              width: '290px',
              height: '580px',
              background: '#000',
              borderRadius: '40px',
              padding: '12px',
              boxShadow: `0 25px 60px rgba(0,0,0,0.5), 0 0 40px ${theme.primary}20, inset 0 0 0 2px #333`
            }}
          >
            {/* Phone Notch */}
            <div style={{
              width: '110px',
              height: '26px',
              background: '#000',
              borderRadius: '0 0 18px 18px',
              margin: '0 auto 0',
              position: 'relative',
              zIndex: 10
            }} />
            {/* Phone Screen */}
            <div style={{
              width: '100%',
              height: 'calc(100% - 26px)',
              background: '#fff',
              borderRadius: '28px',
              overflow: 'hidden',
              marginTop: '-13px'
            }}>
              {PreviewComponent && <PreviewComponent customization={customization} />}
            </div>
          </motion.div>
        ) : (
          // Desktop Browser Frame
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            style={{
              width: '100%',
              maxWidth: '820px',
              background: '#1e1e1e',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: `0 25px 60px rgba(0,0,0,0.5), 0 0 40px ${theme.primary}15`
            }}
          >
            {/* Browser Chrome */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '12px 14px',
              background: '#2d2d2d',
              borderBottom: '1px solid #3d3d3d'
            }}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ff5f56' }} />
                <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ffbd2e' }} />
                <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#27ca40' }} />
              </div>
              <div style={{
                flex: 1,
                background: '#1e1e1e',
                borderRadius: '6px',
                padding: '8px 14px',
                fontSize: '12px',
                color: '#888',
                fontFamily: "'Segoe UI', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ color: '#27ca40' }}>🔒</span>
                app.{companyName.toLowerCase().replace(/\s+/g, '')}.com/{app?.id}
              </div>
              <ExternalLink size={14} color="#666" />
            </div>
            {/* App Content */}
            <div style={{ height: '420px', overflow: 'hidden' }}>
              {PreviewComponent && <PreviewComponent customization={customization} />}
            </div>
          </motion.div>
        )}
      </div>

      {/* CTA Bar */}
      <div style={{
        padding: '16px 20px',
        background: theme.gradient,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <p style={{ 
            margin: '0 0 4px 0', 
            fontSize: '15px', 
            color: 'white',
            fontWeight: 'bold',
            fontFamily: "'Segoe UI', sans-serif",
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Sparkles size={16} />
            Ready to bring this to life?
          </p>
          <p style={{ 
            margin: 0, 
            fontSize: '12px', 
            color: 'rgba(255,255,255,0.85)',
            fontFamily: "'Segoe UI', sans-serif"
          }}>
            Estimated: <strong>{estimate.time}</strong> • Starting at <strong>{estimate.price}</strong>
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <motion.button
            onClick={() => { playSound(SOUNDS.click); setShowCodeModal(true); }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '12px 20px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: '13px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <Download size={16} />
            Tech Specs
          </motion.button>
          <motion.button
            onClick={() => { playSound(SOUNDS.click); onContact?.(); }}
            whileHover={{ scale: 1.05, boxShadow: '0 4px 25px rgba(255,255,255,0.4)' }}
            whileTap={{ scale: 0.95 }}
            style={{
              padding: '12px 28px',
              background: 'white',
              color: theme.primary,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontFamily: "'Segoe UI', sans-serif",
              fontSize: '14px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)'
            }}
          >
            <Mail size={16} />
            Get Started
          </motion.button>
        </div>
      </div>

      {/* Code/Tech Specs Modal */}
      <AnimatePresence>
        {showCodeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCodeModal(false)}
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(0,0,0,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100,
              backdropFilter: 'blur(4px)'
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '90%',
                maxWidth: '500px',
                background: '#1e1e2e',
                borderRadius: '16px',
                overflow: 'hidden',
                border: `1px solid ${theme.primary}40`,
                boxShadow: `0 25px 50px rgba(0,0,0,0.5), 0 0 40px ${theme.primary}20`
              }}
            >
              {/* Modal Header */}
              <div style={{
                padding: '20px',
                background: theme.gradient,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Package size={22} color="white" />
                </div>
                <div>
                  <h3 style={{ margin: 0, color: 'white', fontSize: '16px', fontWeight: '700' }}>
                    {companyName} - {app?.name}
                  </h3>
                  <p style={{ margin: '2px 0 0', color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
                    Technical Specifications & Estimates
                  </p>
                </div>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '20px' }}>
                {/* Tech Stack */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ 
                    margin: '0 0 12px', 
                    color: 'white', 
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FileCode size={14} color={theme.primary} />
                    Tech Stack
                  </h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {techStack[platform].map(tech => (
                      <span key={tech} style={{
                        padding: '6px 12px',
                        background: 'rgba(255,255,255,0.08)',
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: '#ccc',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Features Included */}
                <div style={{ marginBottom: '20px' }}>
                  <h4 style={{ 
                    margin: '0 0 12px', 
                    color: 'white', 
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <Zap size={14} color={theme.primary} />
                    What's Included
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {[
                      'Full source code & documentation',
                      'Responsive design for all devices',
                      'AI-powered features integration',
                      'User authentication & security',
                      'Analytics dashboard',
                      '3 months of free support'
                    ].map(feature => (
                      <div key={feature} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{
                          width: '18px',
                          height: '18px',
                          borderRadius: '50%',
                          background: `${theme.primary}30`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <Check size={10} color={theme.primary} />
                        </div>
                        <span style={{ fontSize: '12px', color: '#bbb' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Estimates */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  <div style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <Clock size={14} color={theme.primary} />
                      <span style={{ fontSize: '11px', color: '#888' }}>Timeline</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'white' }}>
                      {estimate.time}
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                      <DollarSign size={14} color={theme.primary} />
                      <span style={{ fontSize: '11px', color: '#888' }}>Investment</span>
                    </div>
                    <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: 'white' }}>
                      {estimate.price}
                    </p>
                  </div>
                </div>

                {/* CTA */}
                <motion.button
                  onClick={() => { setShowCodeModal(false); onContact?.(); }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: theme.gradient,
                    color: 'white',
                    border: 'none',
                    borderRadius: '10px',
                    fontSize: '14px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '10px',
                    boxShadow: `0 4px 20px ${theme.primary}40`
                  }}
                >
                  <Mail size={18} />
                  Request a Quote
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
